import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { ExternalLink } from 'lucide-react';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function IndicesAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Find Tickers - Indices API" description="Search global indices with the Indices API. Find major, sector, and thematic index tickers with multiple time frame aggregations."  canonicalUrl="https://axionquant.com/docs/01-market-data/indices/02-find-tickers" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Indices API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The Indices API provides comprehensive stock market index data including major global indices, sector indices, and specialized thematic indices. This API supports multiple time frame aggregations and is essential for market analysis, benchmark comparison, and portfolio performance measurement.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Get All Index Tickers */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /indices/tickers
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves a comprehensive list of all available market indices. This endpoint supports filtering by exchange, providing essential metadata for each index including its full name, ticker symbol, and exchange classification.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800 align-top">exchange</td>
                      <td className="px-4 py-3 text-sm text-gray-700 align-top">string</td>
                      <td className="px-4 py-3 text-sm align-top">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <p className="mb-2">Filter indices by exchange code.</p>
                        <a href="./list#exchange" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs mt-2">
                          view all values
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/indices/tickers', '/indices/tickers?exchange=ASE']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">id <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Unique identifier for the index</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ticker <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Index ticker symbol (e.g., &quot;AXJO&quot;, &quot;AEX&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">name <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Full name of the index</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">exchange <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Exchange where the index is calculated/traded</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="All Index Tickers" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">All Index Tickers</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="indices/tickers"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/indices/find-tickers"></Code>
              </div>
            </div>
          </div>
        </div>

        {/* Get Top Index Gainers */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /indices/gainers
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves the top gaining market indices over a specified lookback period. Returns index ticker symbols with their percentage change, sorted from highest gainer to lowest. Useful for identifying which markets are showing relative strength.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">days</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">Optional</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period in days (default: 5)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">limit</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">Optional</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Maximum number of results to return (default: 10)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/indices/gainers', '/indices/gainers?days=30&limit=20']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ticker <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Index ticker symbol</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">pctchange <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Percentage change over the lookback period</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Index Gainers" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Index Gainers</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="indices/gainers"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/indices/gainers"></Code>
              </div>
            </div>
          </div>
        </div>

        {/* Get Top Index Losers */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /indices/losers
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves the top losing market indices over a specified lookback period. Returns index ticker symbols with their percentage change, sorted from most negative to least negative. Useful for identifying which markets are showing relative weakness.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">days</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">Optional</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period in days (default: 5)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">limit</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">Optional</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Maximum number of results to return (default: 10)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/indices/losers', '/indices/losers?days=30&limit=20']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ticker <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Index ticker symbol</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">pctchange <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Percentage change over the lookback period</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Index Losers" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Index Losers</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="indices/losers"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/indices/losers"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
